import React, {useEffect, useState} from 'react';
import {v4 as uuid} from 'uuid';
import {
  LinearProgressProps,
  LinearProgressVariant,
} from '@contentlab/components/linear-progress';
import LtxScalerFeatureLoader from '@contentlab/components/ltx-scaler-feature-loader';
import useViewTransitionEvent from '@contentlab/hooks/analytics/use-view-transition-event';
import useContentLabPageName from '@contentlab/hooks/use-content-lab-page-name';
import translate from '@/utils/translate';

interface SimulatedProgressBarProps {
  titleText?: string | string[];
  subtitleText?: string;
  footerText?: string;
  loaderVariant?: LinearProgressProps['variant'];
}

function SimulatedProgressLoadingScreen(props: SimulatedProgressBarProps) {
  const {
    titleText = translate('Extracting script'),
    subtitleText = translate('This usually takes 8-12 seconds'),
    footerText,
    loaderVariant = LinearProgressVariant.INDETERMINATE,
  } = props;

  const [progress, setProgress] = useState(0);
  const viewTransition = useViewTransitionEvent();
  const pageName = useContentLabPageName();

  useEffect(() => {
    const presentationId = uuid();
    const startTime = Date.now();
    viewTransition.presented({
      presentation_id: presentationId,
      view_name: 'SimulatedProgressLoadingScreen',
      view_type: 'loader',
      view_location: pageName,
      source: '',
    });

    return () => {
      const endTime = Date.now();
      viewTransition.dismissed({
        interaction_id: uuid(),
        presentation_id: presentationId,
        reason: 'loading finished',
        view_details: `Total presented time: ${endTime - startTime} ms`,
      });
    };
  }, []);

  useEffect(() => {
    if (loaderVariant === LinearProgressVariant.INDETERMINATE) {
      // for indeterminate variant, do not simulate progress
      return () => {};
    }
    const startTime = Date.now();
    const duration = 30000; // 30 seconds in milliseconds

    const interval = setInterval(() => {
      const elapsedTime = Date.now() - startTime;
      const newProgress = Math.min((elapsedTime / duration) * 100, 99);
      setProgress(newProgress);

      if (newProgress >= 100) {
        clearInterval(interval);
      }
    }, 100); // Update every 100ms

    return () => clearInterval(interval);
  }, []);

  return (
    <LtxScalerFeatureLoader
      titleText={titleText}
      subtitleText={subtitleText}
      footerText={footerText}
      loaderVariant={loaderVariant}
      loaderProgress={progress}
    />
  );
}

export default SimulatedProgressLoadingScreen;
